import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Check, X as XMark, Server, Globe, Users, Shield } from 'lucide-react';
import SEO from '@/components/SEO';
import Icon from '@/components/Icon';
import BrandIcon from '@/components/BrandIcon';
import { workspaceFeatures, channels, site, useCases, serviceQuality, customerStats, featureHighlights, scalability, comparison } from '@/data/content';

interface FeatureItem {
  title: string;
  description: string;
  icon: string;
}

function FeatureCard({ feature }: { feature: FeatureItem }) {
  return (
    <div className="group rounded-3xl border border-slate-100 bg-white p-6 shadow-lg shadow-slate-200/40 transition-all duration-300 hover:-translate-y-2 hover:scale-[1.01] hover:shadow-2xl hover:shadow-slate-300/50">
      <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 text-brand-700 shadow-inner transition group-hover:from-brand-600 group-hover:to-brand-700 group-hover:text-white">
        <Icon name={feature.icon} className="h-7 w-7" />
      </div>
      <h3 className="text-lg font-extrabold text-slate-900">{feature.title}</h3>
      <p className="mt-3 text-sm leading-relaxed text-slate-600">{feature.description}</p>
    </div>
  );
}

export default function Features() {
  const allFeatures = useMemo(
    () => workspaceFeatures.flatMap((cat) => cat.features),
    []
  );
  const categories = useMemo(
    () => workspaceFeatures.map((cat) => cat.category),
    []
  );
  const [active, setActive] = useState('All');
  const filtered =
    active === 'All'
      ? allFeatures
      : workspaceFeatures.find((cat) => cat.category === active)?.features ?? [];
  const activeCategory = workspaceFeatures.find((cat) => cat.category === active);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: allFeatures.map((f, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: f.title,
        description: f.description,
      })),
    },
  ];

  function compareCell(value: boolean | string, highlight = false) {
    if (typeof value === 'string') {
      const isPrice = value.startsWith('$');
      return <span className={`${isPrice ? 'font-bold' : 'font-medium'} ${highlight ? 'text-brand-700' : 'text-slate-700'}`}>{value}</span>;
    }
    if (value === true) return <Check className="mx-auto h-5 w-5 text-green-600" />;
    if (value === false) return <XMark className="mx-auto h-5 w-5 text-slate-300" />;
    return null;
  }

  function compareBadge(value: string) {
    if (value === 'partial') return <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-700">Partial</span>;
    if (value === 'add-on') return <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-bold text-slate-700">Add-on</span>;
    return null;
  }

  return (
    <>
      <SEO
        title="Features"
        description="Explore every feature of Zuri Hub: conversations, tickets, AI, knowledge base, billing, analytics, and more."
        path="/features"
        jsonLd={jsonLd}
      />

      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-24 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/30 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-4xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-4 py-1.5 text-sm font-medium text-slate-300 backdrop-blur">
            Full feature directory
          </span>
          <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            Everything your team can do with <span className="gradient-text">Zuri Hub</span>
          </h1>
          <p className="mt-6 text-lg text-slate-400">
            A complete customer experience toolkit built for fast, modern SaaS teams.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href={site.workspaceUrl}
              target="_blank"
              rel="noreferrer"
              className="group inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-bold text-slate-900 shadow-xl shadow-white/10 transition hover:bg-slate-100"
            >
              Launch Workspace <ArrowUpRight className="h-4 w-4" />
            </a>
            <Link
              to="/book-a-demo"
              className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-7 py-3.5 text-base font-semibold text-white backdrop-blur transition hover:border-brand-500 hover:bg-slate-800"
            >
              Book a Demo <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 py-4 backdrop-blur">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {['All', ...categories].map((cat) => (
              <button
                key={cat}
                onClick={() => setActive(cat)}
                className={`whitespace-nowrap rounded-full px-5 py-2 text-sm font-bold transition ${
                  active === cat
                    ? 'bg-slate-900 text-white shadow-md'
                    : 'border border-slate-200 bg-white text-slate-700 hover:border-brand-300 hover:text-brand-700'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-label="Feature directory">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {active === 'All' ? (
            <>
              <h2 className="mb-6 text-3xl font-bold text-slate-900 sm:text-4xl">All features</h2>
              <p className="mb-10 max-w-2xl text-lg text-slate-600">
                Browse the full set of tools your team gets in one workspace.
              </p>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.map((feature) => (
                  <FeatureCard key={feature.title} feature={feature} />
                ))}
              </div>
            </>
          ) : (
            <>
              <h2 className="mb-6 text-3xl font-bold text-slate-900 sm:text-4xl">{active}</h2>
              <p className="mb-10 max-w-2xl text-lg text-slate-600">{activeCategory?.summary}</p>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.map((feature) => (
                  <FeatureCard key={feature.title} feature={feature} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <section className="bg-slate-950 py-24 text-white" aria-labelledby="use-cases-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="use-cases-heading" className="text-3xl font-extrabold sm:text-4xl">Use cases that close faster</h2>
            <p className="mt-4 text-lg text-slate-400">How teams use Zuri Hub every day.</p>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {useCases.map((uc) => (
              <div
                key={uc.title}
                className="group rounded-3xl border border-slate-800 bg-slate-900/60 p-8 backdrop-blur transition hover:-translate-y-2 hover:bg-slate-800"
              >
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-600 to-brand-400 text-white shadow-lg">
                  <Icon name={uc.icon} className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-extrabold text-white">{uc.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{uc.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="quality-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="quality-heading" className="section-title">Built for service quality</h2>
            <p className="section-subtitle mx-auto">Reliable, fast, and secure by default.</p>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {serviceQuality.map((s) => (
              <div
                key={s.label}
                className="rounded-3xl border border-slate-100 bg-white p-8 text-left shadow-sm transition hover:-translate-y-2 hover:shadow-xl"
              >
                <div className="text-3xl font-extrabold text-slate-900 sm:text-4xl">{s.metric}</div>
                <div className="mt-2 font-bold text-slate-900">{s.label}</div>
                <p className="mt-2 text-sm text-slate-600">{s.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="numbers-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="numbers-heading" className="section-title">By the numbers</h2>
            <p className="section-subtitle mx-auto">Teams trust Zuri Hub with their hardest conversations.</p>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {customerStats.map((s) => (
              <div
                key={s.label}
                className="rounded-3xl border border-slate-100 bg-slate-50 p-8 text-center shadow-sm transition hover:-translate-y-2 hover:shadow-xl"
              >
                <div className="text-3xl font-extrabold text-slate-900 sm:text-4xl">{s.value}</div>
                <div className="mt-2 font-bold text-slate-900">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="highlights-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="highlights-heading" className="section-title">Feature scalability</h2>
            <p className="section-subtitle mx-auto">Built to grow without limits or re-platforming.</p>
          </div>
          <div className="mt-14 grid gap-6 lg:grid-cols-2">
            {featureHighlights.map((h) => (
              <div
                key={h.id}
                className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-2 hover:shadow-xl"
              >
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 text-brand-700 shadow-inner">
                  <Icon name={h.icon} className="h-7 w-7" />
                </div>
                <h3 className="text-2xl font-extrabold text-slate-900">{h.title}</h3>
                <p className="mt-3 text-slate-600">{h.summary}</p>
                <ul className="mt-6 space-y-3">
                  {h.items.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm font-semibold text-slate-700">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="scale-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="scale-heading" className="section-title">How we scale with you</h2>
            <p className="section-subtitle mx-auto">From your first agent to your thousandth on the same platform.</p>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-3">
            {scalability.tiers.map((tier, index) => (
              <div
                key={tier.plan}
                className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-2 hover:shadow-xl"
              >
                <div className="text-sm font-bold text-slate-500">{tier.agents} agents</div>
                <h3 className="mt-1 text-2xl font-extrabold text-slate-900">{tier.plan}</h3>
                <p className="mt-4 text-slate-600">{tier.capabilities}</p>
                <div className="mt-6 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div className={`h-1.5 rounded-full ${['bg-slate-400','bg-brand-600','bg-slate-900'][index]}`} style={{ width: `${(index + 1) * 33}%` }} />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: 'Server', title: 'Auto-scaling cloud', text: 'Zuri Cloud scales compute and storage as your volume grows.' },
              { icon: 'Globe', title: 'Global edge delivery', text: 'Conversations are routed through the fastest nodes worldwide.' },
              { icon: 'Users', title: 'Unlimited contacts', text: 'No contact limits, no hidden fees, no penalties for growth.' },
              { icon: 'Shield', title: 'Enterprise controls', text: 'SSO, audit logs, and custom SLAs when you need them.' },
            ].map((item, i) => {
              const I = [Server, Globe, Users, Shield][i];
              return (
                <div
                  key={item.title}
                  className="rounded-2xl border border-slate-100 bg-slate-50 p-6 shadow-sm transition hover:-translate-y-2 hover:shadow-xl"
                >
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-brand-700 shadow-sm">
                    <I className="h-6 w-6" />
                  </div>
                  <h4 className="font-bold text-slate-900">{item.title}</h4>
                  <p className="mt-2 text-sm text-slate-600">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="compare-heading">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 id="compare-heading" className="section-title">How Zuri Hub compares</h2>
            <p className="section-subtitle mx-auto">See why teams choose us over the usual tools.</p>
          </div>
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-left">
                    <th className="px-6 py-4 font-bold text-slate-700">Feature</th>
                    <th className="px-6 py-4 text-center font-bold text-slate-900">Zuri Hub</th>
                    <th className="px-6 py-4 text-center font-bold text-slate-700">Intercom</th>
                    <th className="px-6 py-4 text-center font-bold text-slate-700">Zendesk</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.rows.map((row) => (
                    <tr key={row.feature} className="border-b border-slate-100 last:border-0">
                      <td className="px-6 py-4 font-medium text-slate-900">{row.feature}</td>
                      <td className="px-6 py-4 text-center">{compareCell(row.zurihub, true)}</td>
                      <td className="px-6 py-4 text-center">
                        {typeof row.intercom === 'string' && ['partial', 'add-on'].includes(row.intercom) ? compareBadge(row.intercom) : compareCell(row.intercom)}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {typeof row.zendesk === 'string' && ['partial', 'add-on'].includes(row.zendesk) ? compareBadge(row.zendesk) : compareCell(row.zendesk)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-950 py-24 text-white" aria-labelledby="channels-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="channels-heading" className="text-3xl font-bold text-white sm:text-4xl">Every channel your customers use</h2>
            <p className="mt-4 text-lg text-slate-400">
              Real brand icons. Real integrations. One inbox.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {channels.map((channel) => (
              <div
                key={channel.name}
                className="flex items-center gap-4 rounded-2xl border border-slate-800 bg-slate-900/60 p-4 backdrop-blur transition hover:-translate-y-1 hover:bg-slate-800"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-800 shadow-sm">
                  <BrandIcon name={channel.brand} className="h-7 w-7" />
                </div>
                <span className="text-base font-bold text-white">{channel.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-600 py-24 text-white" aria-label="Get started with features">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold sm:text-4xl">Start using these features today</h2>
          <p className="mt-4 text-lg text-brand-100">
            No credit card required. Launch a workspace and test every feature for free.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-brand-700 shadow-lg transition hover:bg-slate-100"
          >
            Launch Workspace <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </>
  );
}
